import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  type ChangeEventHandler,
  type ComponentProps,
  type FocusEventHandler,
  type InputHTMLAttributes,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Button } from "../buttons";
import { cn } from "../utils/cn";
import type { TransformFunction, ValidationFunction } from "./types";
import { DEFAULT_TYPING_TIMEOUT, isString } from "./utils";

interface ButtonProps {
  onClick: (value: string) => void;
  label: string;
  type?: ComponentProps<typeof Button>["type"];
}

interface Props
  extends InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement> {
  leftIcon?: IconProp;
  rightButton?: ButtonProps;
  label?: string;
  rows?: number;
  onFinishWriting?: (value: string) => void;
  timeout?: number;
  transformValue?: TransformFunction;
  validate?: ValidationFunction;
  inputClassName?: string;
  onFocus?: () => void;
  editable?: boolean;
}

export interface DynamicInputRef {
  clear: () => void;
  value: string;
  getHeight: () => number;
  adjustHeight: () => void; // Added method
}

type HTMLGeneralInput = HTMLInputElement | HTMLTextAreaElement;

export const DynamicInput = forwardRef<DynamicInputRef, Props>(
  (
    {
      label,
      leftIcon,
      className,
      rows = 1,
      onFinishWriting,
      value: initialValue,
      onBlur,
      onChange,
      disabled,
      timeout = DEFAULT_TYPING_TIMEOUT,
      transformValue,
      validate = () => true,
      rightButton,
      onFocus,
      editable,
      inputClassName,
      ...props
    },
    ref,
  ) => {
    if (initialValue != null && !isString(initialValue))
      throw new Error("value must be a string");

    const value = useRef(initialValue ?? "");
    const [valueState, setValueState] = useState(value.current);
    const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout | null>(
      null,
    );
    const [isValid, setIsValid] = useState(validate(value.current));
    const [isFocused, setIsFocused] = useState(false);

    // Alternative approach using separate refs:
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const didChange = useRef(false);

    const getRef = () => {
      return textareaRef;
    };

    // Add the adjustHeight function that can be reused
    const adjustHeight = useCallback(() => {
      if (textareaRef.current) {
        // Reset height to auto first to handle text removal
        textareaRef.current.style.height = "auto";
        // Set height to scrollHeight to display all content
        textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
      }
    }, []);

    // Use adjustHeight in useEffect for value and rows changes
    useEffect(() => {
      adjustHeight();
    }, [adjustHeight, valueState, rows]);

    // Add new useEffect for window resize
    useEffect(() => {
      // Function to handle resize
      const handleResize = () => {
        adjustHeight();
      };

      // Add event listener
      window.addEventListener("resize", handleResize);

      // Call once on mount to ensure proper initial sizing
      handleResize();

      // Clean up
      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }, [adjustHeight]);

    // Update useImperativeHandle to include adjustHeight
    useImperativeHandle(ref, () => ({
      clear: () => {
        value.current = "";
        setValueState("");
        setIsValid(validate(value.current));
      },
      value: value.current,
      getHeight: () => getRef().current?.scrollHeight ?? 0,
      adjustHeight, // Add this line to expose the method
    }));

    useEffect(() => {
      if (initialValue != null && initialValue !== value.current) {
        value.current = initialValue;
        setValueState(initialValue);
        setIsValid(validate(initialValue));
      }
    }, [validate, initialValue]);

    const handleFinishWriting = useCallback(
      (value: string) => {
        const newValue = transformValue?.(value) ?? value;

        if (
          validate(newValue) &&
          initialValue !== newValue &&
          didChange.current
        )
          onFinishWriting?.(newValue);
        didChange.current = false;
      },
      [initialValue, onFinishWriting, transformValue, validate],
    );

    const handleFocus: FocusEventHandler<HTMLGeneralInput> = useCallback(
      (event) => {
        setIsFocused(true);
        if (onFocus) {
          onFocus();
        }
        // Adjust height when focused
        adjustHeight();
      },
      [onFocus, adjustHeight],
    );

    const handleBlur: FocusEventHandler<HTMLGeneralInput> = useCallback(
      (event) => {
        setIsFocused(false);
        event.stopPropagation();
        handleFinishWriting(value.current);
        onBlur?.(event);
      },
      [handleFinishWriting, onBlur],
    );

    const handleChange: ChangeEventHandler<HTMLGeneralInput> = useCallback(
      (event) => {
        const newValue = event.target.value;
        value.current = newValue;
        setValueState(newValue);
        didChange.current = true;

        if (typingTimeout) clearTimeout(typingTimeout);
        const newTimeout = setTimeout(
          () => handleFinishWriting(newValue),
          timeout,
        );
        setTypingTimeout(newTimeout);
        setIsValid(validate(newValue));

        onChange?.(event);
        // No need to call adjustHeight here, as it will be triggered by the valueState useEffect
      },
      [onChange, handleFinishWriting, typingTimeout, timeout, validate],
    );

    useEffect(() => {
      return () => {
        if (typingTimeout) clearTimeout(typingTimeout);
      };
    }, [typingTimeout]);

    return (
      <div className="relative flex flex-row justify-center">
        <div
          className={cn(
            "absolute -left-2 w-1 h-full transition-all duration-300 rounded-md",
            isFocused ? "opacity-100 bg-gray-300" : "opacity-0 bg-transparent",
          )}
        />
        <div
          className={cn(
            "group flex gap-3 w-full",
            !isValid &&
              "border-red-3 bg-red-1 hover:bg-red-1 focus-within:bg-red-2 hover:focus-within:bg-red-2",
            className,
          )}
        >
          {leftIcon && (
            <FontAwesomeIcon
              icon={leftIcon}
              className="text-black mt-4"
              width={14}
              height={14}
            />
          )}

          <textarea
            ref={textareaRef}
            className={cn(
              "pt-2 text-sm bg-transparent outline-none flex-grow text-black placeholder:text-gray-400 resize-none border-none border-0", // Add transition for smooth font change
              inputClassName,
            )}
            placeholder={label}
            rows={rows}
            disabled={disabled}
            {...props}
            value={valueState}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
          />

          {rightButton && (
            <Button
              disabled={disabled || !isValid}
              className="my-2"
              type={rightButton.type ?? "button"}
              onClick={() =>
                rightButton.onClick(
                  transformValue?.(value.current) ?? value.current,
                )
              }
            >
              {rightButton.label}
            </Button>
          )}
        </div>
      </div>
    );
  },
);

DynamicInput.displayName = "DynamicInput";
